import {
    ILandingGeometry,
    IRailingGeometry,
    IRoomGeometry,
    IStairCalculationResult,
    IStairConfig,
    IStairGeometry,
    IStepGeometry,
    IStringerGeometry,
    ISupportPosts,
    IValidationItem,
} from '@stair-craft/common/util';

import { IStairNorms, RESIDENTIAL_NORMS } from '../norms';
import { evenPitchRailing } from './shared-baluster-count/baluster-count';
import { flightEntryBottom } from './shared-build-turn-supports/turn-support-steps';
import { IHeadroomResult, calculateHeadroom, lowHeadroomSteps } from './shared-calculate-headroom/calculate-headroom';
import { calculateMaterialVolume } from './shared-calculate-material-volume/calculate-material-volume';
import { applyExitRiser } from './shared-exit-riser/exit-riser';
import { generateFlightSteps } from './shared-generate-flight-steps/generate-flight-steps';
import { shareJunctionPosts } from './shared-junction-posts/junction-posts';
import { IPlanPoint } from './shared-plan-geometry/plan-geometry';
import { capRailingPosts } from './shared-post-caps/post-caps';
import { bringPostsToFloor } from './shared-posts-to-floor/posts-to-floor';
import { addRailingPlanks } from './shared-railing-planks/railing-planks';
import { markTurnRisers } from './shared-riser-board/riser-board';
import { settleStairRailings } from './shared-settle-railings/settle-railings';
import { stairPlacement } from './shared-stair-placement/stair-placement';
import { buildTurnSupportPosts } from './shared-turn-support-posts/turn-support-posts';
import { buildTurnStringers } from './u-shape-build-turn-stringers/build-turn-stringers';
import { buildValidation } from './u-shape-build-validation/build-validation';
import { IUShapeLayout, calculateLayout } from './u-shape-calculate-layout/calculate-layout';
import { computeBoundingBox } from './u-shape-compute-bounding-box/compute-bounding-box';
import { computeWarningStepIndices } from './u-shape-compute-warning-step-indices/compute-warning-step-indices';
import { generateFlight2Steps } from './u-shape-generate-flight2-steps/generate-flight2-steps';
import { generateLandingStep } from './u-shape-generate-landing-step/generate-landing-step';
import { generateStringers } from './u-shape-generate-stringers/generate-stringers';
import { generateUShapeRailings } from './u-shape-generate-u-shape-railings/generate-u-shape-railings';
import { generateUpperFloorRailing } from './u-shape-generate-upper-floor-railing/generate-upper-floor-railing';
import { generateWinderSteps } from './u-shape-generate-winder-steps/generate-winder-steps';

export function calculateUShape(config: IStairConfig): IStairCalculationResult {
    const norms: IStairNorms = RESIDENTIAL_NORMS;
    const totalRise: number = config.floorHeight + config.floorThickness;
    const sw: number = config.stairWidth;
    const isRight: boolean = config.turnDirection === 'right';

    // ─── Layout ────────────────────────────────────────────
    const layout: IUShapeLayout = calculateLayout(config, norms);
    const { stepCount, riserHeight, treadDepth, flight1Count, flight2Count, turnStepCount } = layout;

    const turnStartZ: number = flight1Count * treadDepth;
    const flight1Run: number = turnStartZ;
    const flight2Run: number = flight2Count * treadDepth;
    const flight1Rise: number = flight1Count * riserHeight;
    const flight2Rise: number = flight2Count * riserHeight;

    // Turn zone depth differs: landing = sw (walk across), winder = outerR (semicircle bulge)
    const outerR: number = sw + config.flightGap / 2;
    const isWinder: boolean = config.turnType === 'winder';
    // After 180° turn the person exits the landing at the NEAR edge (turnStartZ),
    // so flight 2 always starts at turnStartZ regardless of turn type.
    const f2StartZ: number = turnStartZ;
    const geoMaxZ: number = turnStartZ + (isWinder ? outerR : sw);

    // Flight 2 exit in local Z (may be negative if flight 2 extends past Z=0)
    const exitLocalZ: number = f2StartZ - flight2Run;
    // totalRun = geoMaxZ so the landing aligns with the far edge of the opening.
    // The exit (bottom of flight 2) will be approximately at openingZ.
    const totalRun: number = geoMaxZ;

    // ─── Steps ─────────────────────────────────────────────
    const flight1Steps: IStepGeometry[] = generateFlightSteps(flight1Count, riserHeight, treadDepth, config);

    let turnSteps: IStepGeometry[];
    let landings: ILandingGeometry[];
    const turnIndex: number = flight1Count;

    if (config.turnType === 'landing') {
        const landingStep: IStepGeometry = generateLandingStep(flight1Count, riserHeight, turnStartZ, config, turnIndex);
        turnSteps = [landingStep];
        landings = [
            {
                position: { x: isRight ? 0 : -(sw + config.flightGap), y: landingStep.position.y, z: turnStartZ },
                width: 2 * sw + config.flightGap,
                depth: sw,
                thickness: config.treadThickness,
            },
        ];
    } else {
        turnSteps = generateWinderSteps(flight1Count, riserHeight, turnStartZ, config, turnIndex);
        landings = [];
    }

    const flight2Steps: IStepGeometry[] = generateFlight2Steps(
        flight1Count,
        turnStepCount,
        flight2Count,
        riserHeight,
        treadDepth,
        f2StartZ,
        config,
        turnIndex + turnStepCount
    );

    const allSteps: IStepGeometry[] = [...flight1Steps, ...turnSteps, ...flight2Steps];
    markTurnRisers(allSteps);
    // Flight 2 exits toward −Z: onto the slab when it ends on the opening's near edge with room
    // left before the wall.
    const exitWorldZ: number = config.openingZ + config.openingLength - totalRun + exitLocalZ;
    applyExitRiser(flight2Steps.at(-1), config, Math.abs(exitWorldZ - config.openingZ) < 1 && config.openingZ > 0);

    // ─── Stringers ─────────────────────────────────────────
    const stringers: IStringerGeometry[] = generateStringers(flight1Steps, flight2Steps, config, riserHeight, turnStartZ, f2StartZ);

    // ─── Turn-zone stringers ───────────────────────────────
    stringers.push(...buildTurnStringers(config, turnSteps, riserHeight, treadDepth, turnStartZ, flightEntryBottom(stringers, turnSteps)));

    // ─── Railings ──────────────────────────────────────────
    // One baluster pitch on the flights and the level runs: the going cut into gaps within the norm.
    const railed: IStairConfig = { ...config, railing: evenPitchRailing(config.railing, treadDepth) };
    const stairRailings: IRailingGeometry[] = config.railing.enabled
        ? generateUShapeRailings(flight1Steps, turnSteps, flight2Steps, railed, turnStartZ, f2StartZ, geoMaxZ)
        : [];
    const upperRailings: IRailingGeometry[] =
        config.railing.enabled && config.railing.upperEnabled ? generateUpperFloorRailing(railed) : [];
    // The support posts stand first: the railing settles around them. Where the stair stands in
    // the room tells which outer corners of a winder turn rest on a wall.
    const placement: IPlanPoint = stairPlacement(config, config, totalRun);
    const supports: ISupportPosts | undefined = buildTurnSupportPosts(config, turnSteps, turnStartZ, stairRailings, stringers, placement);
    settleStairRailings(stairRailings, allSteps, config.stringerType, config.railing.balusterSpacing, supports);
    bringPostsToFloor(stairRailings, allSteps, stringers);
    shareJunctionPosts(stairRailings, upperRailings, placement, config.railing.balusterSpacing);
    const railings: IRailingGeometry[] = [...stairRailings, ...upperRailings];

    // ─── Geometry (with bounding box) ──────────────────────
    const geometry: IStairGeometry = {
        stringers,
        railings,
        landings,
        supports,
        steps: allSteps,
        risers: config.risersEnabled,
        boundingBox: computeBoundingBox(config, sw, isRight, geoMaxZ, exitLocalZ, totalRise),
    };

    // ─── Derived values ────────────────────────────────────
    const stairAngle: number = Math.atan2(riserHeight, treadDepth) * (180 / Math.PI);
    const flight1MarchLen: number = Math.sqrt(flight1Rise ** 2 + flight1Run ** 2);
    const flight2MarchLen: number = Math.sqrt(flight2Rise ** 2 + flight2Run ** 2);
    const totalMarchLength: number = flight1MarchLen + flight2MarchLen;
    addRailingPlanks(railings, stringers, allSteps, config, placement, supports);
    capRailingPosts(railings);
    const headroomResult: IHeadroomResult = calculateHeadroom(allSteps, config, placement);
    const headroom: number = headroomResult.headroom;
    const blondelValue: number = 2 * riserHeight + treadDepth;

    // ─── Material estimation ───────────────────────────────
    const { woodVolumeM3, estimatedWeightKg, estimatedCostUsd } = calculateMaterialVolume(
        allSteps,
        stepCount,
        riserHeight,
        flight1MarchLen,
        flight2MarchLen,
        railings,
        config,
        supports
    );

    // ─── Room geometry ─────────────────────────────────────
    const room: IRoomGeometry = {
        roomLength: config.roomLength,
        roomWidth: config.roomWidth,
        floorHeight: config.floorHeight,
        floorThickness: config.floorThickness,
        openingLength: config.openingLength,
        openingWidth: config.openingWidth,
        openingX: config.openingX,
        openingZ: config.openingZ,
    };

    // ─── Validation ────────────────────────────────────────
    const validation: IValidationItem[] = buildValidation(config, norms, riserHeight, treadDepth, stairAngle, headroom, sw);
    const isValid: boolean = validation.every((v: IValidationItem) => v.severity !== 'error');

    const warningStepIndices: number[] = computeWarningStepIndices(
        allSteps,
        config,
        placement,
        treadDepth,
        lowHeadroomSteps(headroomResult, norms.headroomMin),
        flight1Count,
        sw
    );

    return {
        riserHeight: Math.round(riserHeight * 10) / 10,
        treadDepth: Math.round(treadDepth * 10) / 10,
        stairAngle: Math.round(stairAngle),
        totalRun: Math.round(totalRun),
        headroom: Math.round(headroom),
        blondelValue: Math.round(blondelValue),
        totalMarchLength: Math.round(totalMarchLength),
        woodVolumeM3: Math.round(woodVolumeM3 * 1000) / 1000,
        estimatedWeightKg: Math.round(estimatedWeightKg),
        estimatedCostUsd: Math.round(estimatedCostUsd),
        placementValid: warningStepIndices.length === 0,
        stepCount,
        flight1Count,
        flight2Count,
        turnStepCount,
        room,
        geometry,
        validation,
        isValid,
        warningStepIndices,
    };
}
